import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { fetchOrderById, updateOrder } from "../../api/OrderAPIs";
import { fetchDesignByCode } from "../../api/DesignAPIs";
import { fetchTypes, fetchCaseStylesForType } from "../../api/TypeAPIs";
import { displayAr } from "../Catalog/catalogUi";
import { toast } from "react-toastify";
import { Loader, Search, Trash2, X } from "lucide-react";

const PLACEHOLDER_PHONES = new Set(["غير معروف", "unknown phone", "unknown"]);

const typeSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "44px",
    borderRadius: "0.75rem",
    borderColor: state.isFocused ? "#7c3aed" : "#d1d5db",
    boxShadow: "none",
    direction: "rtl",
    fontSize: "16px",
  }),
  menu: (provided) => ({ ...provided, direction: "rtl", zIndex: 10060 }),
  menuPortal: (provided) => ({ ...provided, zIndex: 10060 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#7c3aed" : state.isFocused ? "#f3e8ff" : "white",
    color: state.isSelected ? "white" : "#111827",
    textAlign: "right",
  }),
  singleValue: (provided) => ({ ...provided, textAlign: "right" }),
  placeholder: (provided) => ({ ...provided, textAlign: "right", color: "#9ca3af" }),
};

const normalizeOptionalPhone = (value) => {
  const phone = String(value || "").trim();
  if (!phone || PLACEHOLDER_PHONES.has(phone.toLowerCase())) return "";
  return phone;
};

const mapServerDetail = (detail) => ({
  orderDetailsId: detail.orderDetailsId ?? detail.OrderDetailsId ?? null,
  designId: detail.designId ?? detail.DesignId ?? null,
  typeId: detail.typeId ?? detail.TypeId ?? null,
  caseStyleId: detail.caseStyleId || detail.CaseStyleId || detail.caseStyle?.caseStyleId || null,
  quantity: detail.quantity ?? detail.Quantity ?? 1,
  price: detail.price ?? detail.Price ?? 0,
});

const toEditPayload = (order, details, totalAmount) => ({
  name: order.name || "",
  phoneNumber: order.phoneNumber || "",
  phoneNumber2: normalizeOptionalPhone(order.phoneNumber2),
  totalAmount: Number(totalAmount ?? order.totalAmount) || 0,
  discount: Number(order.discount) || 0,
  profit: Number(order.profit) || 0,
  shippingAmount: Number(order.shippingAmount) || 0,
  note: order.note || "",
          changeNoteImageCount: Number(order.changeNoteImageCount || 0),
  address: {
    state: order.address?.state || "",
    city: order.address?.city || "",
    apartment: order.address?.apartment || "",
    fullAddress: order.address?.fullAddress || "",
  },
  orderDetails: details.map((detail) => ({
    orderDetailsId: detail.orderDetailsId || null,
    designId: detail.designId ? Number(detail.designId) : null,
    typeId: detail.typeId ? Number(detail.typeId) : null,
    caseStyleId: Number(detail.caseStyleId) || null,
    quantity: Number(detail.quantity) || 1,
    price: Number(detail.price) || 0,
  })),
});

const saveOrderLines = async (orderId, mutateDetails, totalAmount) => {
  const order = await fetchOrderById(orderId);
  const details = (order.orderDetails || []).map(mapServerDetail);
  const nextDetails = mutateDetails(details, order);
  if (!nextDetails.length) {
    throw new Error("لازم تفضل قطعة واحدة على الأقل");
  }
  return updateOrder(orderId, toEditPayload(order, nextDetails, totalAmount));
};

function Sheet({ title, children, onClose, busy, footer }) {
  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      dir="rtl"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div className="bg-white w-full max-h-[92dvh] sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col min-h-0 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b shrink-0">
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} disabled={busy} className="p-2 rounded-xl hover:bg-gray-100 disabled:opacity-50">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto overflow-x-hidden p-4 space-y-3 flex-1 min-h-0">{children}</div>
        {footer && (
          <div className="shrink-0 border-t bg-gray-50 p-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function PieceForm({ initial, types, onCancel, onSubmit, submitLabel, busy }) {
  const [designCode, setDesignCode] = useState(initial.designCode || "");
  const [designId, setDesignId] = useState(initial.designId || "");
  const [designLabel, setDesignLabel] = useState(initial.designLabel || "");
  const [typeId, setTypeId] = useState(initial.typeId ? String(initial.typeId) : "");
  const [caseStyleId, setCaseStyleId] = useState(initial.caseStyleId ? String(initial.caseStyleId) : "");
  const [styles, setStyles] = useState([]);
  const [searching, setSearching] = useState(false);

  const typeOptions = useMemo(
    () =>
      (types || []).map((type) => {
        const id = type.typeId || type.TypeId || type.id;
        return { value: String(id), label: type.name || type.Name || `Type #${id}` };
      }),
    [types]
  );

  useEffect(() => {
    let cancelled = false;
    if (!typeId) {
      setStyles([]);
      return undefined;
    }
    fetchCaseStylesForType(typeId)
      .then((rows) => {
        if (!cancelled) setStyles(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setStyles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [typeId]);

  const lookupDesign = async () => {
    const code = designCode.trim();
    if (!code) return;
    setSearching(true);
    try {
      const design = await fetchDesignByCode(code);
      const id = design?.designId || design?.DesignId || design?.id;
      const name = design?.Name || design?.name || "";
      if (!id) {
        toast.error(`لم يتم العثور على ديزاين بالكود: ${code}`);
        return;
      }
      setDesignId(id);
      setDesignLabel(name || `Design #${id}`);
      setDesignCode(design?.code || design?.Code || code);
      toast.success(`تم ربط الديزاين: ${name || id}`);
    } catch (err) {
      toast.error(err.message || "فشل البحث عن الديزاين");
    } finally {
      setSearching(false);
    }
  };

  const submit = () => {
    if (!Number(designId) || !typeId) {
      toast.error("اختار الديزاين ونوع الموبايل");
      return;
    }
    onSubmit({
      designId: Number(designId),
      typeId: Number(typeId),
      caseStyleId: Number(caseStyleId) || null,
    });
  };

  return (
    <>
      {initial.currentType && (
        <div className="rounded-xl bg-purple-50 border border-purple-100 px-3 py-2">
          <p className="text-xs text-purple-700 font-bold">النوع الحالي</p>
          <p className="text-sm text-gray-800 break-words">{initial.currentType}</p>
          {initial.designLabel && (
            <p className="text-xs text-gray-500 mt-1 break-words">
              {initial.designLabel}
              {initial.designCode ? ` · ${initial.designCode}` : ""}
            </p>
          )}
        </div>
      )}

      <label className="block text-sm">
        <span className="block mb-1 font-medium">الديزاين</span>
        <div className="flex gap-2 min-w-0">
          <input
            className="min-w-0 flex-1 border rounded-xl px-3 py-2 text-base"
            value={designCode}
            onChange={(e) => setDesignCode(e.target.value)}
            placeholder="كود الديزاين"
          />
          <button
            type="button"
            onClick={lookupDesign}
            disabled={searching || busy}
            className="shrink-0 px-3 py-2 rounded-xl bg-orange-500 text-white text-sm font-bold flex items-center gap-1 disabled:opacity-50"
          >
            {searching ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
            بحث
          </button>
        </div>
        {designLabel && <p className="text-xs text-gray-500 mt-1 break-words">{designLabel}</p>}
      </label>

      <label className="block text-sm">
        <span className="block mb-1 font-medium">نوع الموبايل</span>
        <Select
          options={typeOptions}
          value={typeOptions.find((option) => option.value === String(typeId)) || null}
          onChange={(selected) => {
            setTypeId(selected?.value || "");
            setCaseStyleId("");
          }}
          placeholder="ابحث واختر نوع الموبايل..."
          isSearchable
          isClearable
          styles={typeSelectStyles}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuPosition="fixed"
          noOptionsMessage={() => "لا توجد نتائج"}
        />
      </label>

      <label className="block text-sm">
        <span className="block mb-1 font-medium">نوع الكفر</span>
        <select
          className="w-full border rounded-xl px-3 py-2 text-base bg-white"
          value={caseStyleId}
          onChange={(e) => setCaseStyleId(e.target.value)}
          disabled={!typeId}
        >
          <option value="">بدون</option>
          {styles.map((style) => (
            <option key={style.caseStyleId} value={style.caseStyleId}>
              {displayAr(style.name)}
            </option>
          ))}
        </select>
      </label>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} disabled={busy} className="w-full sm:w-auto px-4 py-2.5 rounded-xl border bg-white">
          إلغاء
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-purple-600 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy && <Loader size={16} className="animate-spin" />}
          {submitLabel}
        </button>
      </div>
    </>
  );
}

const OrderPieceDialogs = ({
  editTarget,
  deleteTarget,
  addOrderNumber,
  priceBump,
  onCloseEdit,
  onCloseDelete,
  onCloseAdd,
  onClosePrice,
  onChanged,
  onAdded,
}) => {
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [wantsPrice, setWantsPrice] = useState(false);
  const [increase, setIncrease] = useState("");

  useEffect(() => {
    if (!editTarget && !addOrderNumber) return undefined;
    let cancelled = false;
    fetchTypes()
      .then((rows) => {
        if (!cancelled) setTypes(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) toast.error("فشل تحميل أنواع الموبايلات");
      });
    return () => {
      cancelled = true;
    };
  }, [editTarget, addOrderNumber]);

  useEffect(() => {
    setWantsPrice(false);
    setIncrease("");
  }, [priceBump]);

  const saveEdit = async (patch) => {
    setBusy(true);
    try {
      const updated = await saveOrderLines(editTarget.orderNumber, (details) => {
        let found = false;
        const next = details.map((detail) => {
          if (Number(detail.orderDetailsId) !== Number(editTarget.item.orderDetailsId)) return detail;
          found = true;
          return { ...detail, ...patch };
        });
        if (!found) throw new Error("القطعة مش موجودة في الأوردر");
        return next;
      });
      toast.success("تم تعديل القطعة");
      onChanged?.(updated);
      onCloseEdit();
    } catch (err) {
      toast.error(err.message || "فشل تعديل القطعة");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      const updated = await saveOrderLines(deleteTarget.orderNumber, (details) =>
        details.filter((detail) => Number(detail.orderDetailsId) !== Number(deleteTarget.item.orderDetailsId))
      );
      toast.success("تم حذف القطعة");
      onChanged?.(updated);
      onCloseDelete();
    } catch (err) {
      toast.error(err.message || "فشل حذف القطعة");
    } finally {
      setBusy(false);
    }
  };

  const saveAdd = async (patch) => {
    setBusy(true);
    try {
      const updated = await saveOrderLines(addOrderNumber, (details) => [
        ...details,
        {
          orderDetailsId: null,
          designId: patch.designId,
          typeId: patch.typeId,
          caseStyleId: patch.caseStyleId,
          quantity: 1,
          price: 0,
        },
      ]);
      toast.success("تمت إضافة القطعة");
      onAdded?.(updated);
    } catch (err) {
      toast.error(err.message || "فشل إضافة القطعة");
    } finally {
      setBusy(false);
    }
  };

  const applyPrice = async () => {
    const added = Number(increase);
    if (!Number.isFinite(added) || added <= 0) {
      toast.error("اكتب رقم الزيادة");
      return;
    }
    const nextTotal = Number(priceBump.oldTotal) + added;
    setBusy(true);
    try {
      const updated = await saveOrderLines(priceBump.orderNumber, (details) => details, nextTotal);
      toast.success(`تم تعديل سعر الأوردر إلى ${nextTotal} ج.م`);
      onChanged?.(updated);
      onClosePrice();
    } catch (err) {
      toast.error(err.message || "فشل تعديل السعر");
    } finally {
      setBusy(false);
    }
  };

  const increaseNumber = Number(increase);
  const priceReady = Number.isFinite(increaseNumber) && increaseNumber > 0;
  const newPrice = priceReady ? Number(priceBump?.oldTotal || 0) + increaseNumber : null;

  return (
    <>
      {editTarget && (
        <Sheet title="تعديل القطعة" onClose={onCloseEdit} busy={busy}>
          <PieceForm
            key={editTarget.item.orderDetailsId}
            initial={{
              currentType: editTarget.item.type,
              designCode: editTarget.item.code && editTarget.item.code !== "None" ? editTarget.item.code : "",
              designId: editTarget.item.designId,
              designLabel: editTarget.item.name,
              typeId: editTarget.item.typeId,
              caseStyleId: editTarget.item.caseStyleId,
            }}
            types={types}
            busy={busy}
            submitLabel="حفظ القطعة"
            onCancel={onCloseEdit}
            onSubmit={saveEdit}
          />
        </Sheet>
      )}

      {deleteTarget && (
        <Sheet
          title="تأكيد الحذف"
          onClose={onCloseDelete}
          busy={busy}
          footer={
            <>
              <button type="button" onClick={onCloseDelete} disabled={busy} className="w-full sm:w-auto px-4 py-2.5 rounded-xl border bg-white">
                إلغاء
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={busy}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-red-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busy ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                تأكيد الحذف
              </button>
            </>
          }
        >
          <p className="text-sm text-gray-700">هل أنت متأكد من حذف القطعة دي من الأوردر؟</p>
          <div className="rounded-xl bg-red-50 border border-red-100 px-3 py-2 text-sm">
            <p className="font-bold text-red-800 break-words">{deleteTarget.item.name}</p>
            <p className="text-gray-700 break-words">{deleteTarget.item.type}</p>
            <p className="font-mono text-xs text-gray-500">{deleteTarget.item.code}</p>
          </div>
        </Sheet>
      )}

      {addOrderNumber && (
        <Sheet title="إضافة قطعة" onClose={onCloseAdd} busy={busy}>
          <PieceForm
            key={`add-${addOrderNumber}`}
            initial={{ designCode: "", designId: "", designLabel: "", typeId: "", caseStyleId: "" }}
            types={types}
            busy={busy}
            submitLabel="إضافة القطعة"
            onCancel={onCloseAdd}
            onSubmit={saveAdd}
          />
        </Sheet>
      )}

      {priceBump && (
        <Sheet title="تعديل سعر الأوردر" onClose={onClosePrice} busy={busy}>
          <p className="text-sm text-gray-700">تمت إضافة القطعة. هل حابب تعدل السعر؟</p>
          <p className="text-sm">
            السعر الحالي: <span className="font-bold">{priceBump.oldTotal} ج.م</span>
          </p>
          {!wantsPrice ? (
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button type="button" onClick={onClosePrice} className="w-full sm:w-auto px-4 py-2.5 rounded-xl border bg-white">
                لا
              </button>
              <button
                type="button"
                onClick={() => setWantsPrice(true)}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-purple-600 text-white font-bold"
              >
                أيوه، عدّل السعر
              </button>
            </div>
          ) : (
            <>
              <label className="block text-sm">
                <span className="block mb-1 font-bold">عايز تزود كام؟</span>
                <input
                  type="number"
                  min="0"
                  inputMode="decimal"
                  className="w-full border rounded-xl px-3 py-2 text-base"
                  value={increase}
                  onChange={(e) => setIncrease(e.target.value)}
                  placeholder="0"
                />
              </label>
              <p className="text-sm">
                السعر الجديد:{" "}
                <span className="font-bold text-green-700">{newPrice == null ? "—" : `${newPrice} ج.م`}</span>
              </p>
              <button
                type="button"
                onClick={applyPrice}
                disabled={busy || !priceReady}
                className="w-full px-4 py-2.5 rounded-xl bg-green-600 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busy && <Loader size={16} className="animate-spin" />}
                تعديل سعر الأوردر لـ {newPrice == null ? "السعر الجديد" : `${newPrice} ج.م`}
              </button>
            </>
          )}
        </Sheet>
      )}
    </>
  );
};

export default OrderPieceDialogs;
